<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  id: {
    type: [Number, String],
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  createdAt: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['edit', 'delete']);

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }
  if (props.status === 'in_progress' || props.status === 2) {
    return t('CAMPAIGN.SMS.CARD.STATUS.IN_PROGRESS');
  }
  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});

const formattedCreatedDate = computed(() => {
  if (!props.createdAt) return '';
  // Handle Unix timestamp (number) or date string
  const date =
    typeof props.createdAt === 'number'
      ? new Date(props.createdAt * 1000)
      : new Date(props.createdAt);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
});

const isScheduled = computed(() => {
  // A campaign is scheduled if it's not completed and has a scheduled_at time in the future
  if (props.isLiveChatType || props.status === STATUS_COMPLETED) {
    return false;
  }
  if (!props.scheduledAt) {
    return false;
  }
  const scheduledTime = new Date(props.scheduledAt * 1000);
  return scheduledTime > new Date();
});

const isCompleted = computed(
  () => props.status === STATUS_COMPLETED || props.status === 1
);

const canPause = computed(() => {
  return props.isEnabled && !isCompleted.value;
});

const canResume = computed(() => {
  return !props.isEnabled && !isCompleted.value;
});

const handleViewDetails = () => {
  const accountId = route.params.accountId;
  if (accountId) {
    router.push({
      name: 'campaigns_details',
      params: { accountId, campaignId: props.id },
    });
  }
};

const handlePause = async () => {
  try {
    await store.dispatch('campaigns/pause', props.id);
    useAlert(t('CAMPAIGN.ACTIONS.PAUSE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAMPAIGN.ACTIONS.PAUSE.ERROR_MESSAGE'));
  }
};

const handleResume = async () => {
  try {
    await store.dispatch('campaigns/resume', props.id);
    useAlert(t('CAMPAIGN.ACTIONS.RESUME.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CAMPAIGN.ACTIONS.RESUME.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <div
        v-if="formattedCreatedDate"
        class="flex items-center gap-1 text-xs text-n-slate-11"
      >
        <span class="i-lucide-calendar size-3" />
        <span>{{
          `${t('CAMPAIGN.CARD.CREATED')}: ${formattedCreatedDate}`
        }}</span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-eye"
        :title="t('CAMPAIGN.CARD.VIEW_DETAILS')"
        @click="handleViewDetails"
      />
      <Button
        v-if="canPause"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-pause"
        :title="t('CAMPAIGN.ACTIONS.PAUSE.LABEL')"
        @click="handlePause"
      />
      <Button
        v-if="canResume"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-play"
        :title="t('CAMPAIGN.ACTIONS.RESUME.LABEL')"
        @click="handleResume"
      />
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        :title="t('CAMPAIGN.CARD.EDIT')"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        :title="t('CAMPAIGN.CARD.DELETE')"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
